import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { XCircle, Wand2, Sparkles, Plus, Trash2, Loader2 } from 'lucide-react';
import { apiGet, apiGetAll, apiPost, apiPut, apiDel } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { toast } from 'sonner';
import Pagination from '../../components/Pagination';
import { useUrlTab } from '../../lib/hooks/useUrlState';
import { mprStatusClass } from './stores-constants';
import { MaterialPicker } from '../../components/pickers/MaterialPicker';
import ProcurementWizard from '../../components/procurement/ProcurementWizard';
import { useAuth } from '../../contexts/AuthContext';

// ========== MPR TAB (Material Requisition) — create + track ==========
// MPRs are created, edited and submitted here in Stores. Procurement is
// read-only for MPRs — PM approves/rejects there and converts to RFQ.
export function MPRTab({ projectId, projects }) {
  const [mprs, setMprs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [autogenBusy, setAutogenBusy] = useState(false);

  // Filters + sort + toggle
  const [activeTab, setActiveTab] = useUrlTab('sub', 'active'); // 'active' | 'completed'
  const [filterSearch, setFilterSearch] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGetAll('/mpr');
      let items = data?.items || [];
      const proj = (projects || []).find(p => p.id === projectId);
      if (projectId && proj) items = items.filter(m => m.project_id === projectId || m.project === proj.name);
      setMprs(items);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [projectId, projects]);
  useEffect(() => { load(); }, [load]);

  const runAutogen = async () => {
    if (!projectId) return;
    setAutogenBusy(true);
    try {
      const res = await apiPost('/automation/generate-mprs', {}, { params: { project_id: projectId, horizon_days: 30 } });
      if (res?.created > 0) toast.success(`${res.created} draft MPR${res.created > 1 ? 's' : ''} created from BOQ balances`);
      else toast.info('No pending BOQ balances found to generate MPRs from');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Auto-generate failed'); }
    finally { setAutogenBusy(false); }
  };

  const mpr = detailId ? mprs.find(m => m.id === detailId) : null;

  // ── Completed = Closed / Fully Received; Active = everything else ──
  const COMPLETED_STATUSES = new Set(['closed', 'fully received', 'Closed', 'Fully Received']);
  const tabFiltered = mprs.filter(m =>
    activeTab === 'completed'
      ? COMPLETED_STATUSES.has(m.status || '')
      : !COMPLETED_STATUSES.has(m.status || '')
  );

  // ── Unique project / status options from current data ──
  const projectOptions = [...new Set(tabFiltered.map(m => m.project_name || m.project || '').filter(Boolean))].sort();
  const statusOptions  = [...new Set(tabFiltered.map(m => m.status || '').filter(Boolean))].sort();

  // ── Client-side filter ──
  const q = filterSearch.trim().toLowerCase();
  const filtered = tabFiltered.filter(m => {
    if (filterProject && (m.project_name || m.project || '') !== filterProject) return false;
    if (filterStatus  && (m.status || '') !== filterStatus) return false;
    if (q && !`${m.mpr_no} ${m.project_name || m.project || ''} ${(m.line_items || []).map(l => l.material_name || '').join(' ')}`.toLowerCase().includes(q)) return false;
    return true;
  });

  // ── Sort ──
  const sorted = [...filtered].sort((a, b) => {
    let va = a[sortKey] || '', vb = b[sortKey] || '';
    if (sortKey === 'created_at') { va = va || ''; vb = vb || ''; }
    const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const totalFiltered = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const paginated = sorted.slice(start, start + PAGE_SIZE);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };
  const SortIcon = ({ k }) => sortKey !== k ? <span className="text-slate-400 ml-0.5 text-[10px]">↕</span> : <span className="text-[#3b4872] ml-0.5 text-[10px] font-bold">{sortDir === 'asc' ? '↑' : '↓'}</span>;

  return (
    <div className="space-y-4" data-testid="stores-mpr-tab">
      {mpr && <MPRDetail mpr={mpr} onClose={() => setDetailId(null)} onReload={load} />}
      {showCreate && (
        <ProcurementWizard
          defaultType="mpr"
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}

      <div className="flex justify-between items-start">
        <div className="text-sm text-gray-500 pt-1.5">{sorted.length} of {mprs.length} Material Purchase Requisitions</div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <button onClick={() => setShowCreate(true)} data-testid="mpr-new-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-semibold text-white bg-[#3b4872] rounded-sm hover:bg-[#2d3856] transition-colors">
              <Plus size={12} /> New MPR
            </button>
            {projectId && (
              <button onClick={runAutogen} disabled={autogenBusy} data-testid="mpr-autogen-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-semibold text-[#3b4872] border border-[#3b4872] rounded-sm hover:bg-indigo-50 transition-colors disabled:opacity-50">
                {autogenBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {autogenBusy ? 'Generating…' : 'Auto-generate from BOQ'}
              </button>
            )}
          </div>
          <p className="text-[10px] text-slate-400 mt-1" data-testid="mpr-stores-hint">
            MPRs (Material Purchase Requisitions) request materials to be bought — PM approves here, then Procurement
            raises an RFQ or Direct PO. To issue material already in stock to a site team instead, use the
            separate Material Requisitions (MR) tab.
          </p>
        </div>
      </div>

      {/* ── Active / Completed toggle ── */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {[['active', 'Active'], ['completed', 'Completed']].map(([val, label]) => (
          <button key={val} onClick={() => { setActiveTab(val); setFilterStatus(''); setFilterProject(''); setFilterSearch(''); setPage(1); }}
            className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${activeTab === val ? 'bg-white text-[#3b4872] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid={`mpr-tab-${val}`}>
            {label} ({mprs.filter(m => val === 'completed' ? COMPLETED_STATUSES.has(m.status || '') : !COMPLETED_STATUSES.has(m.status || '')).length})
          </button>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="flex flex-wrap gap-2 items-center">
        <input type="search" placeholder="Search MPR no, project, material…"
          value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
          className="border border-slate-300 rounded-sm px-3 py-1.5 text-sm focus:outline-none focus:border-[#3b4872] w-64"
          data-testid="mpr-filter-search" />
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)}
          className="border border-slate-300 rounded-sm px-3 py-1.5 text-sm focus:outline-none focus:border-[#3b4872] bg-white"
          data-testid="mpr-filter-project">
          <option value="">All Projects</option>
          {projectOptions.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border border-slate-300 rounded-sm px-3 py-1.5 text-sm focus:outline-none focus:border-[#3b4872] bg-white"
          data-testid="mpr-filter-status">
          <option value="">All Statuses</option>
          {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {(filterSearch || filterProject || filterStatus) && (
          <button onClick={() => { setFilterSearch(''); setFilterProject(''); setFilterStatus(''); setPage(1); }}
            className="text-xs text-slate-500 hover:text-rose-600 underline">Clear filters</button>
        )}
      </div>

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-[#3b4872]" onClick={() => toggleSort('mpr_no')}>
                  MPR No <SortIcon k="mpr_no" />
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-[#3b4872]" onClick={() => toggleSort('created_at')}>
                  Date Created <SortIcon k="created_at" />
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-[#3b4872]" onClick={() => toggleSort('project_name')}>
                  Project <SortIcon k="project_name" />
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Items</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Plan Freq.</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-[#3b4872]" onClick={() => toggleSort('status')}>
                  Status <SortIcon k="status" />
                </th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-500">Loading...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-500">
                  {(filterSearch || filterProject || filterStatus) ? 'No results match the current filters.' : activeTab === 'completed' ? 'No completed MPRs yet.' : 'No active MPRs yet.'}
                </td></tr>
              ) : paginated.map(m => {
                const lc = (m.line_items || []).length || 1;
                const createdAt = m.created_at ? formatDate(m.created_at) : '—';
                return (
                  <tr key={m.id} className="hover:bg-gray-50 cursor-pointer" data-testid={`mpr-row-${m.id}`} onClick={() => setDetailId(m.id)}>
                    <td className="px-4 py-3 font-mono text-xs">{m.mpr_no}</td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">{createdAt}</td>
                    <td className="px-4 py-3 text-xs max-w-[180px] truncate" title={m.project_name || m.project || '—'}>{m.project_name || m.project || '—'}</td>
                    <td className="px-4 py-3 text-xs max-w-[200px]">
                      {lc > 1
                        ? <span className="font-medium text-[#3b4872]">{lc} items</span>
                        : <span className="block truncate" title={m.item || m.material_name || ''}>{m.item || m.material_name || '—'}</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-xs">{m.plan_frequency || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${mprStatusClass(m.status)}`} data-testid={`mpr-status-${m.id}`}>{m.status}</span>
                      {m.self_approved && (
                        <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200"
                          title={`Self-approved by ${m.pm_approved_by_name || ''} (${m.self_approved_role || 'exec'})`}
                          data-testid={`mpr-self-approved-${m.id}`}>
                          Self-approved (exec)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right"><button className="text-xs text-[#3b4872] hover:underline" onClick={e => { e.stopPropagation(); setDetailId(m.id); }}>View →</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalFiltered > PAGE_SIZE && (
          <div className="border-t border-slate-200 px-4 py-3">
            <Pagination
              page={safePage} pageSize={PAGE_SIZE} total={totalFiltered}
              start={start} end={Math.min(start + PAGE_SIZE, totalFiltered)}
              onPageChange={setPage} onPageSizeChange={() => {}}
              pageSizeOptions={[]} label="MPRs"
              data-testid="mpr-pagination"
            />
          </div>
        )}
      </CardContent></Card>
    </div>
  );
}

const EDITABLE_STATUSES = new Set(['draft', 'pm rejected', 'rejected']);

// ========== MPR DETAIL MODAL — read-only, with Edit for Draft/PM-Rejected ==========
export function MPRDetail({ mpr, onClose, onReload }) {
  const { user } = useAuth();
  const isSuperAdmin = (user?.role || '').toLowerCase() === 'super_admin';
  const [fulfillment, setFulfillment] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [linkedPOs, setLinkedPOs] = useState([]);
  const [poBusyId, setPoBusyId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadLinkedPOs = useCallback(() => {
    if (!mpr?.id || !isSuperAdmin) return;
    apiGet('/po', { mpr_id: mpr.id, limit: 20 }).then(r => setLinkedPOs(r?.items || [])).catch(() => {});
  }, [mpr?.id, isSuperAdmin]);

  useEffect(() => {
    if (mpr?.id) {
      apiGet(`/mpr/${mpr.id}/fulfillment`).then(r => setFulfillment(r)).catch(() => {});
    }
    loadLinkedPOs();
  }, [mpr?.id, loadLinkedPOs]);

  const lineItems = fulfillment?.line_items || mpr?.line_items || [];
  const canEdit = EDITABLE_STATUSES.has((mpr?.status || '').trim().toLowerCase());

  const startEdit = () => {
    setEditItems(lineItems.length
      ? lineItems.map(li => ({ id: li.id || li.line_id, material_id: li.material_id, material_name: li.material_name, qty: li.qty ?? li.required_qty ?? '', uom: li.uom || '', notes: li.notes || '' }))
      : [{ material_id: '', material_name: '', qty: '', uom: '', notes: '' }]);
    setEditing(true);
  };
  const updateItem = (idx, k, v) => setEditItems(is => is.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const pickMaterial = (idx, mat) => setEditItems(is => is.map((it, i) => i === idx
    ? { ...it, material_id: mat?.id || '', material_name: mat?.name || '', uom: mat?.uom || '' }
    : it));
  const addItem = () => setEditItems(is => [...is, { material_id: '', material_name: '', qty: '', uom: '', notes: '' }]);
  const removeItem = (idx) => setEditItems(is => is.filter((_, i) => i !== idx));
  const validItems = editItems.filter(it => it.material_id && parseFloat(it.qty) > 0);

  const saveChanges = async (andSubmit) => {
    if (!validItems.length) { toast.error('Add at least one item with qty > 0'); return; }
    setSaving(true);
    try {
      await apiPut(`/mpr/${mpr.id}`, {
        line_items: validItems.map(it => ({ id: it.id, material_id: it.material_id, qty: Number(it.qty), uom: it.uom, notes: it.notes })),
        reason: (mpr.status === 'PM Approved' ? (window.prompt('Reason for editing this approved MPR:') || '') : '') || undefined,
      });
      if (andSubmit) { const sr = await apiPost(`/mpr/${mpr.id}/submit`, {}); if (sr?.no_pm_warning) toast.warning(sr.no_pm_warning); }
      toast.success(andSubmit ? 'MPR updated and submitted to PM' : 'MPR updated');
      setEditing(false);
      onReload?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save changes'); }
    finally { setSaving(false); }
  };

  const deletePO = async (po) => {
    const reason = window.prompt(`Delete PO ${po.po_no}? This clears the dependency blocking the MPR delete.\n\nEnter a reason:`);
    if (!reason || !reason.trim()) return;
    setPoBusyId(po.id);
    try {
      await apiDel(`/po/${po.id}`, { data: { reason: reason.trim() } });
      toast.success(`${po.po_no} deleted`);
      loadLinkedPOs();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete PO'); }
    finally { setPoBusyId(null); }
  };

  const deleteMPR = async () => {
    if (linkedPOs.length > 0) {
      toast.error('Delete the linked PO(s) above first, then delete this MPR.');
      return;
    }
    if (!window.confirm(`Permanently delete ${mpr.mpr_no}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const res = await apiDel(`/mpr/${mpr.id}`);
      toast.success(`${res?.deleted_mpr_no || mpr.mpr_no} deleted`);
      onReload?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete MPR'); }
    finally { setDeleting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-3xl my-8" data-testid="mpr-detail-modal">
        <div className="p-5 border-b flex justify-between items-center">
          <div>
            <h2 className="text-base font-bold">{mpr.mpr_no}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {[mpr.project_name || mpr.project, mpr.plan_frequency, mpr.source_basis].filter(Boolean).join(' · ')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${mprStatusClass(mpr.status)}`}>{mpr.status}</span>
              {mpr.self_approved && (
                <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200"
                  title={`Self-approved by ${mpr.pm_approved_by_name || ''} (${mpr.self_approved_role || 'exec'})`}
                  data-testid="mpr-detail-self-approved-badge">
                  Self-approved (exec)
                </span>
              )}
            <Button variant="ghost" size="sm" onClick={onClose}><XCircle className="w-4 h-4" /></Button>
          </div>
        </div>
        {mpr.status === 'PM Rejected' && mpr.rejection_reason && (
          <div className="px-5 pt-4 text-xs text-rose-600 bg-rose-50 border-b border-rose-100 py-2" data-testid="mpr-detail-rejection-reason">
            Rejected: {mpr.rejection_reason}
          </div>
        )}
        {isSuperAdmin && linkedPOs.length > 0 && (
          <div className="px-5 pt-4 text-xs bg-amber-50 border-b border-amber-100 py-3" data-testid="mpr-detail-linked-pos">
            <p className="font-semibold text-amber-800 mb-1.5">
              {linkedPOs.length} Purchase Order{linkedPOs.length > 1 ? 's' : ''} linked — delete {linkedPOs.length > 1 ? 'these' : 'this'} first to delete the MPR:
            </p>
            <div className="space-y-1">
              {linkedPOs.map(po => (
                <div key={po.id} className="flex items-center justify-between bg-white border border-amber-200 rounded-sm px-2.5 py-1.5">
                  <span className="font-mono text-slate-700">{po.po_no} <span className="text-slate-400 font-sans">· {po.status}</span></span>
                  <button onClick={() => deletePO(po)} disabled={poBusyId === po.id} data-testid={`mpr-detail-delete-po-${po.id}`}
                    className="flex items-center gap-1 px-2 py-1 text-[11px] font-display font-semibold text-rose-600 border border-rose-300 rounded-sm hover:bg-rose-50 disabled:opacity-40">
                    {poBusyId === po.id ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />} Delete PO
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="p-5">
          {!editing ? (
            <table className="w-full text-sm" data-testid="mpr-detail-items-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Material</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-600">Required</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-600">Received</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-600">Balance</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-600">%</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {lineItems.map((li, idx) => (
                  <tr key={idx} data-testid={`mpr-detail-row-${idx}`}>
                    <td className="px-3 py-2 max-w-[180px]">
                      <span className="block truncate" title={li.material_name || li.material_id || ''}>
                        {li.material_name || li.material_id || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">{li.required_qty ?? li.qty ?? '—'} {li.uom}</td>
                    <td className="px-3 py-2 text-right">{li.received_qty ?? 0} {li.uom}</td>
                    <td className="px-3 py-2 text-right">{((li.required_qty ?? li.qty ?? 0) - (li.received_qty ?? 0))} {li.uom}</td>
                    <td className="px-3 py-2 text-right">{li.fulfillment_pct != null ? `${li.fulfillment_pct.toFixed(1)}%` : '0%'}</td>
                  </tr>
                ))}
                {lineItems.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-4 text-center text-gray-500 text-xs">No line items</td></tr>
                )}
              </tbody>
            </table>
          ) : (
            <div data-testid="mpr-detail-edit-form">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-slate-500">Edit line items, then save or save &amp; re-submit to PM.</p>
                <button onClick={addItem} data-testid="mpr-edit-add-item"
                  className="flex items-center gap-1 text-xs font-display font-semibold text-[#3b4872] border border-[#3b4872] px-2.5 py-1 rounded-sm hover:bg-indigo-50">
                  <Plus size={11} /> Add Item
                </button>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {editItems.map((it, idx) => (
                  <div key={idx} className="p-3 rounded-sm border border-slate-200 bg-slate-50" data-testid={`mpr-edit-item-${idx}`}>
                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-5">
                        <MaterialPicker value={it.material_id} displayName={it.material_name} onSelect={mat => pickMaterial(idx, mat)} testid={`mpr-edit-mat-${idx}`} />
                      </div>
                      <div className="col-span-2">
                        <input type="number" min="0" step="any" placeholder="Qty" value={it.qty} onChange={e => updateItem(idx, 'qty', e.target.value)}
                          data-testid={`mpr-edit-qty-${idx}`}
                          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:outline-none" />
                      </div>
                      <div className="col-span-2">
                        <input placeholder="UoM" value={it.uom} onChange={e => updateItem(idx, 'uom', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:outline-none" />
                      </div>
                      <div className="col-span-2">
                        <input placeholder="Notes" value={it.notes} onChange={e => updateItem(idx, 'notes', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:outline-none" />
                      </div>
                      <div className="col-span-1 flex items-center justify-end pt-1">
                        <button onClick={() => removeItem(idx)} disabled={editItems.length === 1}
                          className="p-1 text-rose-400 hover:text-rose-600 disabled:opacity-30 rounded-sm"><Trash2 size={13} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="border-t p-4 bg-slate-50 flex items-center justify-between">
          {!editing ? (
            <>
              <p className="text-xs text-slate-500 italic">
                {canEdit
                  ? 'You can edit this MPR and re-submit it to PM.'
                  : <>This MPR has been submitted — approval, rejection and RFQ conversion happen in <a href="/app/procurement?tab=MPR" className="underline text-[#3b4872]">Procurement</a>.</>}
              </p>
              <div className="flex items-center gap-2">
                {isSuperAdmin && (
                  <button onClick={deleteMPR} disabled={deleting || linkedPOs.length > 0} data-testid="mpr-detail-delete-btn"
                    title={linkedPOs.length > 0 ? 'Delete the linked PO(s) above first' : 'Permanently delete this MPR (super_admin only)'}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-display font-semibold text-rose-600 border border-rose-300 rounded-sm hover:bg-rose-50 disabled:opacity-40">
                    {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Delete MPR
                  </button>
                )}
                {canEdit && (
                  <button onClick={startEdit} data-testid="mpr-detail-edit-btn"
                    className="px-4 py-2 text-xs font-display font-semibold text-[#3b4872] border border-[#3b4872] rounded-sm hover:bg-indigo-50">
                    Edit MPR
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2 justify-end w-full">
              <button onClick={() => setEditing(false)} disabled={saving}
                className="px-4 py-2 text-xs border border-slate-300 rounded-sm font-display font-semibold hover:border-slate-400 disabled:opacity-40">
                Cancel
              </button>
              <button onClick={() => saveChanges(false)} disabled={saving} data-testid="mpr-detail-save-btn"
                className="px-4 py-2 text-xs border border-slate-300 rounded-sm font-display font-semibold hover:border-slate-400 disabled:opacity-40">
                Save Changes
              </button>
              <button onClick={() => saveChanges(true)} disabled={saving} data-testid="mpr-detail-submit-btn"
                className="flex items-center gap-1.5 px-4 py-2 text-xs bg-[#3b4872] text-white rounded-sm font-display font-semibold hover:bg-[#2c3658] disabled:opacity-40">
                {saving ? <><Loader2 size={13} className="animate-spin" /> Saving…</> : <>Save & Submit to PM <Wand2 size={12} /></>}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
